import React, { ComponentProps, useContext, useEffect, useRef, useState } from 'react';
import ContextButton from '../../components/ContextButton';
import ColumnSelectorOption from '../../components/view/ColumnSelectorOption';
import SelectableList from '../../components/SelectableList';
import * as ViewHelper from '../../utils/ViewHelper';
import componentStyles from '../../styles/partials/_column-selector.css';
import btnStyles from '../../styles/partials/_button.css';
import * as entities from '../../graphql/entities';
import _orderBy from 'lodash/orderBy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ColumnSelectorOptionContext from '../Context/ColumnSelectorOptionContext';
import ViewContext from '../Context/ViewContext';
import { IFilterAttributeObject, Maybe } from '../../graphql/schema/p4types';
import { IObjectField } from '../../graphql/schema/getQueryFields';
import { IconButton } from '@fluentui/react';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

type IGlyphProps = {
  columns: Array<ISelectorColumn>;
  setColumns: (value: ((prevState: Array<ISelectorColumn>) => Array<ISelectorColumn>) | Array<ISelectorColumn>) => void;
  updateColumns: (closePopup: boolean) => void;
};

const TotalGlyph = ({ columns, setColumns, updateColumns }: IGlyphProps) => {
  const { selected, isNumeric, enabled, option } = useContext(ColumnSelectorOptionContext);

  const onGlyphClick = () => {
    const { index, total } = option;
    const newColumns = columns.slice();
    const column = newColumns[index];
    column.total = !total;

    newColumns[index] = column;
    setColumns(newColumns);

    updateColumns(false);
  };

  return (
    <Glyph
      display={selected && isNumeric}
      onClick={onGlyphClick}
      colour={enabled ? '#00dff5' : '#686868'}
      icon={'calculator'}
    />
  );
};

const ListFilterGlyph = ({ columns, setColumns, updateColumns }: IGlyphProps) => {
  const { option } = useContext(ColumnSelectorOptionContext);
  const { currentView } = useContext(ViewContext);

  const filterIndex = currentView?.filterAttributeObjects?.findIndex(
    (filterAttributeObject: IFilterAttributeObject) => filterAttributeObject.fieldName === option.fieldName
  );

  const currentFilterStyle =
    filterIndex >= 0 ? currentView?.filterAttributeObjects[filterIndex].filterStyle : 'DEFAULT';

  const onGlyphClick = () => {
    const { index } = option;
    const newColumns = columns.slice();
    const column = newColumns[index];
    column.selected = true;
    column.filterStyle = currentFilterStyle === 'LIST' ? 'DEFAULT' : 'LIST';
    column.label = option.description;
    newColumns[index] = column;
    setColumns(newColumns);

    updateColumns(true);
  };

  return (
    <Glyph
      display={columns[option.index]?.type === 'REFERENCE'}
      onClick={onGlyphClick}
      colour={currentFilterStyle === 'LIST' ? '#00dff5' : '#686868'}
      icon={'list-ul'}
    />
  );
};

type ISelectorColumn = {
  title: string;
  fieldName: string;
  type: string;
  objectType: string;
  virtualType: string;
  selected: boolean;
  formatter: any;
  total: boolean;
  totalCalculation?: string;
  filterStyle?: any;
  label?: string;

  /**
   * If true, this column will always be displayed (It cannot be disabled via the column selector, but can be moved/re-sized).
   */
  required?: boolean;
};

// Sort columns into required order for display - selected columns at the top
const loadColumns = ({
  allowImage,
  allowObjectColumns,
  totalFieldNames,
  availableColumns,
  selectedFieldNames,
}: {
  availableColumns: Array<IAvailableColumn>;
  selectedFieldNames: Array<string>;
  totalFieldNames: Array<string>;
  allowObjectColumns: boolean;
  allowImage: boolean;
}): Array<ISelectorColumn> => {
  let excludedPath;

  // Exclude any columns without fieldName or title
  // If allowObjectColumns is set exclude ProjectedList (and all columns below it) and transient columns
  const excludedPaths: Array<any> = [];
  const trimmedColumns: { [key: string]: typeof orderedColumns[number] } = {};
  availableColumns.forEach((column) => {
    if (
      !column.title ||
      !column.fieldName ||
      column.name.endsWith('Concurrency Version') ||
      column.name.endsWith('Id') ||
      column.name === 'Document Id' ||
      column.name.endsWith('Attribute Index') //Bit specific - But just tidying pricing attributes which leave an unnecessary 'index' entry in the selector.
    ) {
      return;
    }
    if (allowObjectColumns) {
      if ((column.isTransient || column.virtualType === 'ProjectedList') && !column.sortable) {
        excludedPaths.push(column.fieldName);
        return;
      }
    }
    if (!allowImage && column.fieldName === 'image') {
      excludedPaths.push(column.fieldName);
      return;
    }
    if (excludedPaths.length) {
      excludedPath = excludedPaths.find((path) => {
        return column.fieldName.startsWith(path);
      });
      if (excludedPath) {
        return;
      }
    }

    trimmedColumns[column.fieldName] = column;
  });

  // Add selected columns first in order given in selectedFieldNames
  const included: { [key: string]: boolean } = {};
  let columns: Array<ISelectorColumn> = [];
  selectedFieldNames.forEach((fieldName) => {
    included[fieldName] = true;
    const column = trimmedColumns[fieldName];
    if (column) {
      columns.push({
        title: column.title,
        fieldName: column.fieldName,
        type: column.type,
        objectType: column.objectType,
        virtualType: column.virtualType,
        selected: true,
        formatter: column.formatter,
        total: totalFieldNames.findIndex((value) => value === column.fieldName) >= 0,
        totalCalculation: column.totalCalculation,
        required: column.required,
      });
    }
  });
  columns = _orderBy(columns, 'title', 'asc');

  const orderedColumns = _orderBy(availableColumns, 'title', 'asc');

  // Add all other columns not selected (as long as they have a title and fieldName)
  orderedColumns.forEach((column) => {
    if (!included[column.fieldName] && trimmedColumns[column.fieldName]) {
      columns.push({
        title: column.title,
        fieldName: column.fieldName,
        type: column.type,
        objectType: column.objectType,
        virtualType: column.virtualType,
        selected: false,
        formatter: column.formatter,
        total: totalFieldNames.findIndex((value) => value === column.fieldName) >= 0,
        totalCalculation: column.totalCalculation,
        required: column.required,
      });
    }
  });

  return columns;
};

export const Glyph = ({
  display,
  icon,
  onClick,
  colour,
  style,
}: {
  display: boolean;
  icon: any;
  onClick: ComponentProps<typeof FontAwesomeIcon>['onClick'];
  colour: any;
  style?: React.CSSProperties;
}) => {
  return display ? (
    <FontAwesomeIcon icon={icon} onClick={onClick} color={colour} style={style || { marginTop: '5px' }} />
  ) : null;
};

//Likely incomplete.
export type IAvailableColumn = {
  title: string;
  fieldName: string;
  type: string;
  objectType: string;
  virtualType: string;
  name: string;
  isTransient?: boolean;
  sortable?: boolean;
  formatter?: any;
  totalCalculation?: string;

  /**
   * If true, this column will always be displayed (It cannot be disabled via the column selector, but can be moved/re-sized).
   */
  required?: boolean;

  /**
   * If true (And the grid is set for enableRowSelect + enableCellAutoFocus), this column will be editable when clicked into.
   */
  editable?: boolean;

  /**
   * @deprecated - Upcoming P4-10993 should remove the need for this (Which will help with the inconsistency with which its' available).
   * Availability may be inconsistent - Used for certain filters (i.e. Warehouse-List filters 'Stourport Pick - Free Stock')
   */
  idList?: Array<number>;
};

type IColumnSelectorProps = {
  availableColumns: Array<IAvailableColumn>;
  selectedFieldNames: Array<string>;
  totalFieldNames: Array<string>;
  label: string;
  allowObjectColumns: boolean;
  allowImage: boolean;
  immediateUpdate: boolean;
  tabIndex: number;
  extraClass?: string;
  alignLeft: boolean;
  showClearAll: boolean;
  clearAllText: string;
  numericFields?: Maybe<Array<IObjectField>>;
  updateColumns: (newCols: Array<ISelectorColumn>) => void;
  'data-testid': string;

  /**
   * If provided, a button is included to allow resetting 'all' configured column widths on the view.
   * (Only expected in the columns container, not the filters container).
   */
  onResetClick?: (() => void) | undefined;
};

const ColumnSelector = (props: IColumnSelectorProps) => {
  const styles: { [key: string]: string } = {};
  Object.assign(styles, componentStyles, btnStyles);

  const filterInputRef = useRef<any>();
  const columnSelectRef = useRef<any>();

  const { allowImage, allowObjectColumns, availableColumns, selectedFieldNames, totalFieldNames } = props;

  const [columns, setColumns] = useState<Array<ISelectorColumn>>(
    loadColumns({ allowImage, allowObjectColumns, availableColumns, selectedFieldNames, totalFieldNames })
  );
  const [filterText, setFilterText] = useState('');
  const [showAll, setShowAll] = useState(false);

  const fieldNamesDependency = props.selectedFieldNames.join(',');

  //Destructuring selectedFieldNames and availableColumns for dependency comparison because these arrays are re-created on each render.
  //The result of which is that comparing the parent array reloads columns on every parent re-render when nested in a modal.
  useEffect(() => {
    setColumns(
      loadColumns({
        allowImage: props.allowImage,
        allowObjectColumns: props.allowObjectColumns,
        totalFieldNames: props.totalFieldNames,
        availableColumns: props.availableColumns,
        selectedFieldNames: props.selectedFieldNames,
      })
    );
  }, [
    fieldNamesDependency,
    props.allowImage,
    props.allowObjectColumns,
    props.availableColumns,
    props.availableColumns.length,
    props.selectedFieldNames,
    props.totalFieldNames,
  ]);

  const moveColumn = (fieldName: string, newIndex: number) => {
    setColumns(ViewHelper.moveColumn(columns, fieldName, newIndex));
  };

  // Toggle column selected state when clicked
  const onColumnClick = ({ index, selected, total }: { index: number; selected: boolean; total: boolean }) => {
    const newColumns = columns.slice();
    const column = newColumns[index];
    column.selected = !selected;
    column.total = total;
    newColumns[index] = column;
    setColumns(newColumns);

    if (props.immediateUpdate) {
      updateColumns(column.selected, newColumns);
    }
  };

  const onClearAll = () => {
    const newColumns = columns.slice();
    for (let i = 0; i < newColumns.length; i++) {
      if (newColumns[i].selected) {
        const column = Object.assign({}, newColumns[i]);
        column.selected = false;
        newColumns[i] = column;
      }
    }
    setColumns(newColumns);
    props.updateColumns(newColumns);
  };

  // Event handler for filter input
  const handleFilter = (e: any) => {
    e.preventDefault();
    setFilterText(e.target.value);
  };

  // Event handler for clear filter click
  const handleClearFilter = (e: any) => {
    e.preventDefault();
    setFilterText('');
    filterInputRef.current?.focus?.();
  };

  // Event handler for showing full column details for nested objects
  const handleShowMore = (e: any) => {
    e.preventDefault();
    setShowAll(true);
  };

  // Event handler for clicking Update button
  const handleUpdate = (e: any) => {
    e.preventDefault();
    updateColumns();
  };

  // Sort columns with selected columns first, apply position attribute to each column object and call
  // props.updateColumns
  const updateColumns = (closePopup = true, updatedColumns = columns) => {
    if (closePopup) {
      close();
    }

    // Shuffle selected columns to top of list
    const newColumns = updatedColumns.filter((column) => {
      return column.selected;
    });
    Array.prototype.push.apply(
      newColumns,
      updatedColumns.filter((column) => {
        return !column.selected;
      })
    );

    let i = 0;
    props.selectedFieldNames.forEach((value) => {
      const columnIndex = newColumns.findIndex((column) => value === column.fieldName);
      if (columnIndex >= 0) {
        //Swap the field to it's expected position
        const target = newColumns[i];
        newColumns[i++] = newColumns[columnIndex];
        newColumns[columnIndex] = target;
      }
    });
    newColumns.forEach((column, index) => {
      newColumns[index] = Object.assign({}, column, { position: index });
    });

    props.updateColumns(newColumns);
  };

  // Event handler for Cancel button
  const handleCancel = (e: any) => {
    e.preventDefault();
    close();
  };

  const close = () => {
    if (columnSelectRef.current) {
      columnSelectRef.current.popoutClose();
    }
  };

  const options: ComponentProps<typeof SelectableList>['options'] = [];
  let count;
  let moreDetail;
  let objectTitle: string;
  const lowerCaseFilterText = filterText.toLowerCase();
  columns.forEach((column, index) => {
    // Filter any columns that do not match the current search
    if (lowerCaseFilterText) {
      if (!column.title.toLowerCase().includes(lowerCaseFilterText)) {
        return;
      }
    }

    // When not searching limit the display of items to the first level of nested objects
    if (!lowerCaseFilterText) {
      count = (column.title.match(/->/g) || []).length;
      if (count > 1 && !showAll) {
        moreDetail = 1;
        return;
      }
    }

    // If column is an object prepare to add a sub title within the list
    if (column.type === 'Object') {
      objectTitle = column.title;
    }

    // Ignore Object columns unless allowed - Object columns are only included if they have a STANDARD datasource
    // associated with them
    if (column.type === 'Object') {
      if (!props.allowObjectColumns || !(entities as { [key: string]: any })[column.objectType]) {
        return;
      }
    }
    if (column.type === 'Enum' && !props.allowObjectColumns) {
      return;
    }

    const isNumeric =
      !props.numericFields || !!props.numericFields.find((value) => value.fieldName === column.fieldName);

    const title = column.title.replace(`${objectTitle}->`, '');
    options.push({
      index: index,
      fieldName: column.fieldName,
      description: title,
      selected: column.selected,
      moveColumn: moveColumn,
      subTitle: false,
      isNumeric: isNumeric,
      total: column.total,
      filterStyle: column.filterStyle,
      required: column.required,
    });
  });

  const buttons = props.immediateUpdate ? null : (
    <div className={styles.colBtnContainer}>
      <a
        className={styles.btnPrimarySmall}
        href="#"
        onClick={handleUpdate}
        data-testid={`${props['data-testid']}_update`}
      >
        Update
      </a>
      <a className={styles.btnSecondarySmall} href="#" onClick={handleCancel}>
        Cancel
      </a>
    </div>
  );

  const clearInput = filterText ? (
    <div className={styles.clearInput} onClick={handleClearFilter} data-testid={`${props.label}_columns_clear`}>
      <i className="icon">highlight_off</i>
    </div>
  ) : null;

  const loadMore = moreDetail ? (
    <div className={styles.showBtnContainer}>
      <button className={styles.btnSecondarySmall} onClick={handleShowMore}>
        Show More Columns
      </button>
    </div>
  ) : null;

  const clearAll = props.showClearAll ? (
    <div className={styles.clearBtnContainer}>
      <button className={styles.clearBtn} onClick={onClearAll} type="button" data-testid="filter_clear_all_button">
        {props.clearAllText}
      </button>
    </div>
  ) : null;

  return (
    <ContextButton
      ref={columnSelectRef}
      label={props.label}
      buttonClass="btnBorder"
      tabIndex={props.tabIndex}
      alignLeft={props.alignLeft}
    >
      <div className={styles.filterContainer}>
        <input
          className={`${styles.filterInput} ignore-react-onclickoutside`}
          onChange={handleFilter}
          placeholder="Search columns"
          value={filterText}
          ref={filterInputRef}
          data-testid={`${props.label}_columns_input`}
        />
        {clearInput}
        {props.onResetClick && (
          <TooltipHost content={'Resets all active columns to their original width'} id={'reset-column-size-btn'}>
            <IconButton iconProps={{ iconName: 'FitPage' }} onClick={props.onResetClick} />
          </TooltipHost>
        )}
      </div>
      {clearAll}
      <SelectableList
        options={options}
        optionClass={ColumnSelectorOption}
        data-testid={props.label}
        onSelect={onColumnClick}
        glyph={
          props.label === 'Filters' ? (
            <ListFilterGlyph columns={columns} setColumns={setColumns} updateColumns={updateColumns} />
          ) : (
            <TotalGlyph columns={columns} setColumns={setColumns} updateColumns={updateColumns} />
          )
        }
        listLabel={props.label}
      />
      {loadMore}
      {buttons}
    </ContextButton>
  );
};

ColumnSelector.defaultProps = {
  selectedFieldNames: [],
  totalFieldNames: [],
  label: 'Columns',
  allowObjectColumns: false,
  allowImage: true,
  alignLeft: false,
  immediateUpdate: false,
  showClearAll: true,
  clearAllText: 'Clear All Filters',
};

export default ColumnSelector;
